// segments.ts — everything the admin screens say about a board's segmentation,
// as one pure function of (leaderboard, bucket summary).
//
// The screens it feeds — Overview and Cycles — show the same combined top,
// which on a segmented board is a list no player ever sees: scores are ranked
// and rewards handed out inside a single (tier_key, bucket_id) pair. The
// wording below is what stops the screen from claiming otherwise, so it lives
// in one place: two copies of the same sentence come apart on the first edit.
//
// Nothing here grows with the board. A cycle of 20 000 rooms produces the same
// four strings as a cycle of one, which is the whole design constraint: the
// admin can be told how segmented a board is without ever enumerating segments.

import type { BucketSummary, Cycle, Leaderboard } from "./types";

/**
 * The board as ONE cycle's rows have to be read: its own settings, with the two
 * fields the cycle carries a stamp of put back to what they were when it was
 * cut. The TypeScript twin of the server's `cycleRules`.
 *
 * Every function below asks the BOARD what its rows are partitioned by, which
 * was the same question until segmentation could move. It stopped being: a board
 * that switches to personal lists drops its own `bucket_size` in the same patch,
 * so the week of rooms still being played — and every archived week before it —
 * would answer «no segmentation» to all of them and be drawn as a flat table
 * with no Bucket column and no counts.
 *
 * It takes over only where the board has LEFT the mode the cycle was cut in.
 * While the two agree the board's settings are the live ones and are meant to
 * be: rooms can be resized or switched on mid-cycle on a tier board.
 */
export function cycleShape<
  T extends Pick<Leaderboard, "segmentation" | "bucket_size">,
>(lb: T, cycle: Pick<Cycle, "segmentation" | "bucket_size"> | undefined): T {
  if (!cycle || cycle.segmentation === lb.segmentation) return lb;
  return {
    ...lb,
    segmentation: cycle.segmentation,
    bucket_size: cycle.bucket_size ?? null,
  };
}

/** Which shape of segment the board has. Decides the column and the nouns. */
export type SegmentKind =
  /** segmentation=bucket — the room IS the segment. */
  | "bucket"
  /** segmentation=tier with a bucket_size — the segment is (league, room). */
  | "tier_rooms"
  /** segmentation=tier with rooms off — the segment is the league. */
  | "tier";

/** One run of note text. `strong` marks the phrase the sentence turns on. */
export interface NotePart {
  text: string;
  strong?: boolean;
}

export interface SegmentDescriptor {
  kind: SegmentKind;
  /** "Segments" hero value. "—" while the aggregate is still in flight. */
  statValue: string;
  /** Hero sub-line under the value. Empty when there is nothing to say yet. */
  statSub: string;
  /** Suffix after the cycle id in the Top 5 card header. */
  scopeLabel: string;
  /** The `412 buckets of 50` item in the Cycles panel head. */
  headLabel: string;
  /** Header of the segment column between Entity and Score. */
  columnLabel: string;
  /** Footnote under the Top 5 card header. Empty ⇒ render no note. */
  note: NotePart[];
  /**
   * Footnote under the Cycles panel head. Harsher than `note` on purpose: that
   * table has a Rank column, and the rank in it is the position in the whole
   * cycle — not the one the player sees inside their own segment.
   */
  cyclesNote: NotePart[];
}

/**
 * Whether the board's segmentation is counted in rows of `leaderboard_buckets`,
 * i.e. whether the summary endpoint has anything to answer. A tier board with
 * rooms off has its segment count in `tier_config` already, and a `none` board
 * has no segments at all — neither should cost a request.
 */
export function needsBucketSummary(
  lb: Pick<Leaderboard, "segmentation" | "bucket_size">,
): boolean {
  if (lb.segmentation === "bucket") return true;
  return lb.segmentation === "tier" && lb.bucket_size != null;
}

/**
 * Whether the country view is a thing this board can have at all — migration
 * 0056's `lb_country_split_check` said in TypeScript: a country is a filter
 * over ONE ordering, and only these boards have one. A `guild_internal` board
 * already ranks inside a single guild, and a bucket or tier board already ranks
 * inside a partition; «country × league × room» is a third dimension nobody
 * built.
 *
 * It lives here, beside the other reading of segmentation, because two screens
 * ask it: the form decides whether to OFFER the toggle, and the config card
 * whether the board's answer is worth stating at all. Two copies of one CHECK
 * come apart on the first edit — and they come apart into a form that offers
 * what the database refuses.
 */
export function countrySplitAllowed(
  lb: Pick<Leaderboard, "scope" | "segmentation">,
): boolean {
  return (
    (lb.scope === "global" || lb.scope === "guild_aggregated") &&
    lb.segmentation === "none"
  );
}

/** Flatten note parts to plain text (assertions, `title`, aria). */
export function noteText(parts: NotePart[]): string {
  return parts.map((p) => p.text).join("");
}

function fmt(n: number): string {
  return n.toLocaleString("en-US");
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

/** `4 tiers`, `1 tier`. */
function tiersPhrase(tiers: number): string {
  return `${fmt(tiers)} ${plural(tiers, "tier", "tiers")}`;
}

/**
 * The whole segmentation story for one board, or null when there is none to
 * tell — on `segmentation=none` every caller must render exactly what it
 * rendered before this existed.
 *
 * `summary` is undefined while the aggregate loads, failed, or was never asked
 * for; the descriptor then carries the column (which comes off the entries
 * themselves) and stays silent about counts rather than guessing them.
 */
export function describeSegments(
  lb: Pick<Leaderboard, "scope" | "segmentation" | "bucket_size" | "tier_config">,
  summary: BucketSummary | undefined,
): SegmentDescriptor | null {
  const size = lb.bucket_size ?? null;
  const tiers = lb.tier_config?.tiers.length ?? 0;

  if (lb.segmentation === "bucket") {
    // A bucket board without a size is not a state the backend can produce
    // (validateCreate rejects it), and there is nothing truthful to say about
    // rooms whose size is unknown.
    if (size === null) return null;
    // What a ROW of this board is. `guild_aggregated` + `bucket` used to be a
    // pair the API refused, so every sentence below was written about players
    // and could not be wrong; a board of teams reaches the same sentences now,
    // and «players» in them would name the wrong entity on the two screens
    // that print them (Overview and Cycles). The tier branches take no scope:
    // a league is a player's league, and that pair is still refused.
    return bucketDescriptor(size, summary, lb.scope === "guild_aggregated");
  }
  if (lb.segmentation === "tier") {
    return size === null
      ? tierDescriptor(tiers)
      : tierRoomsDescriptor(size, tiers, summary);
  }
  return null;
}

/** Column + value only: counts unknown, so the copy says nothing about them. */
function quiet(
  kind: SegmentKind,
  columnLabel: string,
  statValue: string,
): SegmentDescriptor {
  return {
    kind,
    statValue,
    statSub: "",
    scopeLabel: "",
    headLabel: "",
    columnLabel,
    note: [],
    cyclesNote: [],
  };
}

function bucketDescriptor(
  size: number,
  summary: BucketSummary | undefined,
  /** The board ranks guilds, so every row of it is a team, not a player. */
  guildRows: boolean,
): SegmentDescriptor {
  if (!summary) return quiet("bucket", "Bucket", "—");
  const { count, members, full } = summary;
  // No rooms yet — an open cycle nobody has submitted to. "0" is the honest
  // stat; every phrase below is about rooms that exist, so none of them apply.
  if (count === 0) return quiet("bucket", "Bucket", "0");

  // The nouns the three sentences below turn on. One place, because the same
  // entity has to be named the same way in all of them.
  const rows = guildRows ? "Teams" : "Players";
  const oneRow = guildRows ? "a team" : "a player";
  // Singular subject: a team is an «it», a player keeps the singular «they»
  // the sentence has always used. The plural sentence takes «their» either
  // way, which is why the two are separate words rather than one.
  const oneRowOwn = guildRows ? "its own" : "their own";

  const single = count <= 1;
  const bucketsPhrase = `${fmt(count)} ${plural(count, "bucket", "buckets")}`;
  // The one-bucket case is the one that answers the question the screen kept
  // raising: this list is real, and it stays real only until the room fills.
  const sameBucket: NotePart[] = [
    { text: `All ${fmt(members)} ${rows.toLowerCase()} sit in the same bucket, so this ` },
    { text: "is", strong: true },
    {
      text:
        " the ranking they see. It stops being one as soon as the bucket fills" +
        " and a second one opens.",
    },
  ];
  const combined: NotePart[] = [
    { text: "Combined across all " },
    { text: bucketsPhrase, strong: true },
  ];
  return {
    kind: "bucket",
    statValue: fmt(count),
    statSub: single
      ? `bucket of ${fmt(size)} · ${fmt(members)} seated`
      : `buckets of ${fmt(size)} · ${fmt(full)} full`,
    scopeLabel: single ? "1 bucket" : `all ${bucketsPhrase}`,
    headLabel: `${bucketsPhrase} of ${fmt(size)}`,
    columnLabel: "Bucket",
    note: single
      ? sameBucket
      : [
          ...combined,
          {
            text:
              `. ${rows} are ranked and rewarded inside their own bucket, so` +
              " nobody sees this list.",
          },
        ],
    // With one bucket the cycle-wide rank IS the rank the row sees, so the
    // Cycles tab has nothing harsher to add and reuses the Overview wording.
    cyclesNote: single
      ? sameBucket
      : [
          ...combined,
          {
            text:
              `. Rank is the position in the whole cycle — not the rank ${oneRow}` +
              ` sees inside ${oneRowOwn} bucket, which is where scores are` +
              " ranked and rewards are handed out.",
          },
        ],
  };
}

function tierRoomsDescriptor(
  size: number,
  tiers: number,
  summary: BucketSummary | undefined,
): SegmentDescriptor {
  if (!summary) return quiet("tier_rooms", "Tier · Room", "—");
  const { count, full } = summary;
  if (count === 0) return quiet("tier_rooms", "Tier · Room", "0");

  const roomsPhrase = `${fmt(count)} ${plural(count, "room", "rooms")}`;
  const combined: NotePart[] = [
    { text: "Combined across " },
    { text: tiersPhrase(tiers), strong: true },
    { text: " and " },
    { text: roomsPhrase, strong: true },
  ];
  return {
    kind: "tier_rooms",
    statValue: fmt(count),
    statSub: `${plural(count, "room", "rooms")} of ${fmt(size)} in ${tiersPhrase(
      tiers,
    )} · ${fmt(full)} full`,
    scopeLabel: count === 1 ? "1 room" : `all ${roomsPhrase}`,
    headLabel: `${roomsPhrase} of ${fmt(size)}`,
    columnLabel: "Tier · Room",
    note: [
      ...combined,
      {
        text:
          ". Players are ranked and rewarded inside their own room, so nobody" +
          " sees this list.",
      },
    ],
    cyclesNote: [
      ...combined,
      {
        text:
          ". Rank is the position in the whole cycle — not the rank a player" +
          " sees inside their own room, which is where scores are ranked and" +
          " rewards are handed out.",
      },
    ],
  };
}

/**
 * Rooms off: the segments are the leagues themselves, and how many there are is
 * already in `tier_config`. Counting their population would mean a GROUP BY
 * over every score in the cycle on each open, which is exactly the cost this
 * screen refuses to pay.
 */
function tierDescriptor(tiers: number): SegmentDescriptor {
  const combined: NotePart[] = [
    { text: "Combined across " },
    { text: tiersPhrase(tiers), strong: true },
  ];
  return {
    kind: "tier",
    statValue: fmt(tiers),
    statSub: `${plural(tiers, "tier", "tiers")} · rooms off`,
    scopeLabel: "",
    headLabel: "",
    columnLabel: "Tier",
    note: [
      ...combined,
      {
        text:
          ". Players are ranked and rewarded inside their own tier, so nobody" +
          " sees this list.",
      },
    ],
    cyclesNote: [
      ...combined,
      {
        text:
          ". Rank is the position in the whole cycle — not the rank a player" +
          " sees inside their own tier, which is where scores are ranked and" +
          " rewards are handed out.",
      },
    ],
  };
}
